<template>
    <div>
        <Loader v-if="loading" />
        <div v-else>
            <div class="container-fluid mt-45">
                <Breadcrumbs />

                <div class="row mt-4">
                    <div class="col">
                        <div class="card">
                            <div class="card-body">
                                <div class="row">
                                    <div class="col-md-6">
                                        <h5 class="mb-0">Patient Details</h5>
                                    </div>

                                    <div class="col-md-6 text-right center-mobile">
                                        <button @click="updatePatient()" onclick="this.blur();" class="btn btn-success btn-block-mobile sm-shadow ml-md-2 mt-4 mt-md-0">Save changes</button>
                                        <span v-if="Object.keys(errors).length || Object.keys(addressErrors).length" class="text-sm d-block text-danger mt-2">There are errors on the page. Please review them and try again</span> 
                                    </div>
                                </div>
                                
                                <div class="row justify-content-between mt-3">
                                    <div class="col-lg-6 pr-lg-6">
                                        <div class="form-group row">
                                            <label for="title" class="col-sm-4">Title <span class="text-danger">*</span></label>
                                            <div class="col-sm-8">
                                                <select type="text" class="custom-select" id="title" v-model="patient.title">
                                                    <option :value="Salutation.MR">Mr</option>
                                                    <option :value="Salutation.MRS">Mrs</option>
                                                    <option :value="Salutation.MISS">Miss</option>
                                                    <option :value="Salutation.MS">Ms</option>
                                                    <option :value="Salutation.DR">Dr</option>
                                                    <option :value="Salutation.SIR">Sir</option>
                                                    <option :value="Salutation.PROFESSOR">Professor</option>
                                                </select>
                                                <small v-if="errors && errors.title" class="text-danger">
                                                    {{ errors.title[0] }}
                                                </small>
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <label for="first-name" class="col-sm-4">First name <span class="text-danger">*</span></label>
                                            <div class="col-sm-8">
                                                <input type="text" class="form-control text-capitalize" :class="{'border border-danger' : errors && errors.firstName}" id="first-name" v-model="patient.firstName" autocomplete="new-password">
                                                <small v-if="errors && errors.firstName" class="text-danger">
                                                    {{ errors.firstName[0] }}
                                                </small>
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <label for="middle-names" class="col-sm-4">Middle names</label>
                                            <div class="col-sm-8">
                                                <input type="text" class="form-control text-capitalize" id="middle-names" v-model="patient.middleNames">
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <label for="surname" class="col-sm-4">Surname <span class="text-danger">*</span></label>
                                            <div class="col-sm-8">
                                                <input type="text" class="form-control text-capitalize" :class="{'border border-danger' : errors && errors.surname}" id="surname" v-model="patient.surname" autocomplete="new-password">
                                                <small v-if="errors && errors.surname" class="text-danger">
                                                    {{ errors.surname[0] }}
                                                </small>
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <label for="dob" class="col-sm-4">DOB <span v-if="organisationId != organisationIdException" class="text-danger">*</span></label>

                                            <div class="col-sm-8">
                                                <div class="row mb-1">
                                                    <div class="col-sm-4">
                                                        <select class="custom-select" :class="{'border border-danger' : errors && errors.dayOfBirth}" v-model="dayOfBirthValue">
                                                            <option v-for="item in 31" :key="item" :value="item">{{ item }}</option>
                                                        </select>
                                                    </div>

                                                    <div class="col-sm-4 mt-1 mt-md-0">
                                                        <select class="custom-select" :class="{'border border-danger' : errors && errors.monthOfBirth}" v-model="monthOfBirthValue">
                                                            <option value="1">January</option>
                                                            <option value="2">February</option>
                                                            <option value="3">March</option>
                                                            <option value="4">April</option>
                                                            <option value="5">May</option>
                                                            <option value="6">June</option>
                                                            <option value="7">July</option>
                                                            <option value="8">August</option>
                                                            <option value="9">September</option>
                                                            <option value="10">October</option>
                                                            <option value="11">November</option>
                                                            <option value="12">December</option>
                                                        </select>
                                                    </div>

                                                    <div class="col-sm-4 mt-1 mt-md-0">
                                                        <select class="custom-select" :class="{'border border-danger' : errors && errors.yearOfBirth}" v-model="yearOfBirthValue">
                                                            <option v-for="item in years" :key="item" :value="item">{{ item }}</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <small v-if="errors && errors.dayOfBirth" class="text-danger d-block">
                                                    {{ errors.dayOfBirth[0] }}
                                                </small>

                                                <small v-if="errors && errors.monthOfBirth" class="text-danger d-block">
                                                    {{ errors.monthOfBirth[0] }}
                                                </small>

                                                <small v-if="errors && errors.yearOfBirth" class="text-danger d-block">
                                                    {{ errors.yearOfBirth[0] }}
                                                </small>

                                                <small v-if="errors && errors.dateOfBirth" class="text-danger d-block">
                                                    {{ errors.dateOfBirth[0] }}
                                                </small>
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <label for="nhs-no" class="col-sm-4">{{ patientIdentifierLabel }} <span class="text-danger">*</span></label>
                                            <div class="col-sm-8">
                                                <div class="d-flex align-items-center">
                                                    <input @focusout="handlePatientIdentifierFocusOut()" :aria-busy="String(isPatientIdentifierBusy)" type="text" class="form-control" :class="{'border border-danger' : errors && errors.identifier}" id="nhs-no" v-model="patient.identifier" autocomplete="new-password">
                                                    <span v-if="isPatientIdentifierBusy" class="span-loading ml-2" role="status"><span class="sr-only">Checking {{ patientIdentifierLabel }}</span></span>
                                                </div>
                                                <small v-if="errors && errors.identifier" class="text-danger">
                                                    {{ errors.identifier[0] }}
                                                </small>
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <label for="phone-no" class="col-sm-4">Phone number <span class="text-danger">*</span></label>
                                            <div class="col-sm-8">
                                                <input type="text" class="form-control" :class="{'border border-danger' : errors && errors.phone}" id="phone-no" v-model="patient.phone" autocomplete="new-password">
                                                <small v-if="errors && errors.phone" class="text-danger">
                                                    {{ errors.phone[0] }}
                                                </small>
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <label for="email" class="col-sm-4">Email <span class="text-danger">*</span></label>
                                            <div class="col-sm-8">
                                                <input type="text" class="form-control" :class="{'border border-danger' : errors && errors.email}" id="email" v-model="patient.email" autocomplete="new-password">
                                                <small v-if="errors && errors.email" class="text-danger">
                                                    {{ errors.email[0] }}
                                                </small>
                                            </div>
                                        </div>

                                        <template v-if="patient.address.countryCode == 'GB'">
                                            <UkAddressComponent
                                                :labelColSize="Number(4)"
                                                :inputColSize="Number(8)"
                                                :showExpandOption="Boolean(false)"
                                            />

                                            <hr />
                                        </template>

                                        <template v-if="patient.address.countryCode == 'US'">
                                            <UsAddressComponent
                                                :labelColSize="Number(4)"
                                                :inputColSize="Number(8)"
                                                :showExpandOption="Boolean(false)"
                                            />

                                            <hr />
                                        </template>

                                        <template v-if="patient.address.countryCode == 'QA'">
                                            <QaAddressComponent
                                                :labelColSize="Number(4)"
                                                :inputColSize="Number(8)"
                                            />

                                            <hr />
                                        </template>

                                        <template v-if="patient.address.countryCode == 'AE'">
                                            <AeAddressComponent
                                                :labelColSize="Number(4)"
                                                :inputColSize="Number(8)"
                                            />

                                            <hr />
                                        </template>

                                        <template v-if="patient.address.countryCode == 'SG'">
                                            <SgAddressComponent
                                                :labelColSize="Number(4)"
                                                :inputColSize="Number(8)"
                                            />

                                            <hr />
                                        </template>
                                    </div>

                                    <div class="col-lg-6 pl-lg-6">
                                        
                                        <div class="form-group row align-items-center">
                                            <label for="height" class="col-sm-4 pt-0">Height <span v-if="organisationId != organisationIdException" class="text-danger">*</span></label>
                                            <div class="col-sm-8">
                                                <div class="row">
                                                    <div class="col-sm-4">
                                                        <label for="centimetres">Centimetres</label>
                                                        <input type="number" min="0" onkeypress="return (event.charCode == 8 || event.charCode == 0 || event.charCode == 13) ? null : event.charCode >= 48 && event.charCode <= 57" id="centimetres" class="form-control" :class="{'border border-danger' : errors && errors.height}" @input="convertCentimetresToFeetAndInches(patient.height)" v-model="patient.height">
                                                        <small v-if="errors && errors.height" class="text-danger">
                                                            {{ errors.height[0] }}
                                                        </small>
                                                    </div>

                                                    <div class="col-sm-2 d-flex justify-content-center align-items-center">
                                                        <p class="mb-0 mt-3 font-weight-bold">OR</p>
                                                    </div>

                                                    <div class="col-sm-3">
                                                        <label for="feet">Feet</label>
                                                        <input type="number" min="0" @input="convertFeetAndInchesToCentimetres(feet, inches, false)" class="form-control" id="feet" v-model="feet">
                                                    </div>

                                                    <div class="col-sm-3">
                                                        <label for="inches">Inches</label>
                                                        <input type="number" min="0" max="11" @input="convertFeetAndInchesToCentimetres(feet, inches, false)" class="form-control" id="inches" v-model="inches">
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <hr>

                                        <div class="form-group row">
                                            <label for="weight" class="col-sm-4 pt-0">Weight <span v-if="organisationId != organisationIdException" class="text-danger">*</span></label>
                                            <div class="col-sm-8">
                                                <div class="row">
                                                    <div class="col-sm-4">
                                                        <label for="kilograms">Kilograms</label>
                                                        <input type="number" min="0" id="kilograms" class="form-control" :class="{'border border-danger' : errors && errors.weight}" @input="convertKilosToStonesAndPounds(patient.weight)" v-model="patient.weight">
                                                        <small v-if="errors && errors.weight" class="text-danger">
                                                            {{ errors.weight[0] }}
                                                        </small>
                                                    </div>

                                                    <div class="col-sm-2 d-flex align-items-center justify-content-center">
                                                        <p class="mb-0 mt-3 font-weight-bold">OR</p>
                                                    </div>

                                                    <div class="col-sm-3">
                                                        <label for="stone">Stone</label>
                                                        <input class="form-control" type="number" min="0" @input="convertStonesAndPoundsToKilos(stones, pounds, false)" v-model="stones" />
                                                    </div>

                                                    <div class="col-sm-3">
                                                        <label for="pounds">Pounds</label>
                                                        <input class="form-control" type="number" max="14" min="0" @input="convertStonesAndPoundsToKilos(stones, pounds, false)" v-model="pounds" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <hr>

                                        <div class="form-group row">
                                            <label for="gender" class="col-sm-4 pt-0">Gender <span class="text-danger">*</span></label>
                                            <div class="col-sm-8">
                                                <div class="custom-radio">
                                                    <div class="form-check">
                                                        <input class="form-check-input" type="radio" name="genderRadios" value="0" v-model="patient.gender">
                                                        <label class="form-check-label">
                                                            Male
                                                        </label>
                                                    </div>
                                                </div>
                                                <div class="custom-radio">
                                                    <div class="form-check">
                                                        <input class="form-check-input" type="radio" name="genderRadios" value="1" v-model="patient.gender">
                                                        <label class="form-check-label">
                                                            Female
                                                        </label>
                                                    </div>
                                                </div>
                                                <div class="custom-radio">
                                                    <div class="form-check">
                                                        <input class="form-check-input" type="radio" name="genderRadios" value="2" v-model="patient.gender">
                                                        <label class="form-check-label">
                                                            Other / prefer not to say
                                                        </label>
                                                    </div>
                                                </div>
                                                <small v-if="errors && errors.gender" class="text-danger">
                                                    {{ errors.gender[0] }}
                                                </small>
                                            </div>
                                        </div>

                                        <hr>

                                        <div class="form-group row">
                                            <label for="ethnicity" class="col-sm-4 pt-0">Ethnicity group <span class="text-danger">*</span></label>
                                            <div class="col-sm-8">
                                                <div class="form-group">
                                                    <label>Primary ethnicity</label>
                                                    <select class="custom-select" :disabled="isBusy" v-model="patient.primaryEthnicity" @change="clearEthnicities(true)">
                                                        <option value=null disabled>Please select an option..</option>
                                                        <option v-for="option in ethnicities" :key="option.code" :value="option">{{ option.label }}</option>
                                                    </select>
                                                    <small v-if="errors && errors.primaryEthnicity" class="text-danger">
                                                        {{ errors.primaryEthnicity[0] }}
                                                    </small>
                                                </div>

                                                <div v-if="patient.primaryEthnicity != null && patient.primaryEthnicity.secondaryEthnicityDefinitions && patient.primaryEthnicity.secondaryEthnicityDefinitions.length" class="form-group">
                                                    <label>Secondary ethnicity</label>
                                                    <select class="custom-select" :disabled="isBusy" v-model="patient.secondaryEthnicity" @change="clearEthnicities(false)">
                                                        <option value=null disabled>Please select an option..</option>
                                                        <option v-for="option in patient.primaryEthnicity.secondaryEthnicityDefinitions" :key="option.code" :value="option">{{ option.label }}</option>
                                                    </select>
                                                    <small v-if="errors && errors.secondaryEthnicity" class="text-danger">
                                                        {{ errors.secondaryEthnicity[0] }}
                                                    </small>
                                                </div>

                                                <div v-if="patient.primaryEthnicity != null && patient.secondaryEthnicity != null && patient.secondaryEthnicity.tertiaryEthnicityDefinitions && patient.secondaryEthnicity.tertiaryEthnicityDefinitions.length" class="form-group">
                                                    <label>Tertiary ethnicity</label>
                                                    <select class="custom-select" :disabled="isBusy" v-model="patient.tertiaryEthnicity">
                                                        <option value=null disabled>Please select an option..</option>
                                                        <option v-for="option in patient.secondaryEthnicity.tertiaryEthnicityDefinitions" :key="option.code" :value="option">{{ option.label }}</option>
                                                    </select>
                                                    <small v-if="errors && errors.tertiaryEthnicity" class="text-danger">
                                                        {{ errors.tertiaryEthnicity[0] }}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>

                                        <hr>

                                        <div class="form-group row">
                                            <label for="address3" class="col-sm-4">Preferred Language</label>
                                            <div class="col-sm-8">
                                                <select :disabled="isBusy" class="custom-select" :class="{'border border-danger' : errors && errors.language}" v-model="patient.preferredLanguage">
                                                    <option disabled selected value="null">Please select a language</option>
                                                    <option v-for="language in languages" :value="language.code" :key="language.id">{{ language.name }}</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <PatientNotes
                    :notes="patientNotes"
                    :patientId="$route.params.id"
                    v-on:reload="getPatientNotes()"
                />
            </div>
        </div>
    </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import axios from 'axios'
import Loader from '../../../Common/components/Loader.vue'
import Breadcrumbs from '@/partials/site-structure/BreadcrumbsComponent.vue'
import PatientNotes from '@/components/PatientNotesComponent.vue'
import WeightConversionMixin from '../../../Common/mixins/WeightConversionMixin'
import Salutation from '../../../Common/enums/Salutation'
import ConfirmPatientDetailsModal from '@/components/modals/ConfirmPatientDetailsModalComponent.vue'
import UkAddressComponent from '../../../Common/components/UkAddressComponent.vue'
import UsAddressComponent from '../../../Common/components/UsAddressComponent.vue'
import QaAddressComponent from '../../../Common/components/QaAddressComponent.vue'
import AeAddressComponent from '../../../Common/components/AeAddressComponent.vue'
import SgAddressComponent from '../../../Common/components/SgAddressComponent.vue'
import AddressComponentValidationMixin from '../../../Common/mixins/AddressComponentValidationMixin'
import {isValidPhoneNumber, phoneNumberTooShortMessage} from '../../../Common/helpers/phoneNumberRules'
import {IDENTIFIER_CHECK_OUTCOME, createPatientIdentifierCheck, hasPatientIdentifierChanged, identifierCheckOutcome, isIdentifierTakenByAnotherPatient, normalisePatientIdentifier} from '@/helpers/patientIdentifierCheck'
import {fetchPatientByIdentifier} from '@/helpers/fetchPatientByIdentifier'
export default {
    name: 'EditPatient',

    components: {
        Loader,
        Breadcrumbs,
        PatientNotes,
        UkAddressComponent,
        UsAddressComponent,
        QaAddressComponent,
        AeAddressComponent,
        SgAddressComponent
    },

    mixins: [
        WeightConversionMixin,
        AddressComponentValidationMixin
    ],
    
    data() {
        return {
            organisationIdException: '9cc86b5d-87a7-44d7-a642-c95ada3c229e',
            loading: true,
            isPatientIdentifierBusy: false,
            isCheckingIdentifier: false,
            originalIdentifier: null,
            stones: null,
            pounds: null,
            feet: null,
            inches: null,
            patient: {},
            patientTests: [],
            patientNotes: [],
            errors: {},
            dayOfBirthValue: null,
            monthOfBirthValue: null,
            yearOfBirthValue: null,
            Salutation,
            ConfirmPatientDetailsModal
        }
    },

    created() {
        this.checkPatientIdentifier = createPatientIdentifierCheck(identifier => fetchPatientByIdentifier(this.organisationId, identifier))
    },

    async mounted() {
        try {
            await this.getPatientDetails()
            await this.getPatientTests()
            await this.getPatientNotes()
            this.expandManualAddress()
        }
        catch {
            this.setNotification({
                type: 'error',
                title: 'Error',
                message: 'Something went wrong while fetching the data. Please try again'
            })
        }
        finally {
            this.loading = false
        }
    },

    computed: {
        ...mapGetters({
            isTrial: 'OrganisationStore/GetIsTrial',
            organisationId: 'UserStore/GetUserOrganisationId',
            organisationCountryCode: 'OrganisationStore/GetMyOrganisationCountryCode',
            myOrganisation: 'OrganisationStore/GetMyOrganisation',
            patientIdentifierLabel: 'OrganisationStore/GetPatientIdentifierLabel',
            patientIdentifierRegex: 'OrganisationStore/GetPatientIdentifierRegex',
            patientIdentifierErrorMessage: 'OrganisationStore/GetPatientIdentifierErrorMessage',
            ethnicities: 'EthnicityStore/GetEthnicities',
            languages: 'LanguageStore/GetLanguages',
            address: 'AddressFormStore/GetAddress',
            addressErrors: 'AddressFormStore/GetAddressErrors'
        }),

        duplicatePatientIdentifierMessage() {
            return `A patient with the same ${this.patientIdentifierLabel} already exists`
        },

        addressComputed: {
            get: function() {
                return `${this.patient.address1+', '}${this.patient.address2 ? this.patient.address2+', ' : ''}${this.patient.address3 ? this.patient.address3+', ' : ''}${this.patient.address4 ? this.patient.address4+', ' : ''}${this.patient.postalCode ? this.patient.postalCode+', ' : ''}${this.patient.country ? this.patient.country : ''}`
            }
        },

        years() {
            var currentYear = new Date().getFullYear(), 
            years = [], 
            startYear = 1920; 

            while ( startYear <= currentYear ) {
                years.push(startYear++);
            }   

            return years;
        },
    },

    methods: {
        ...mapActions({
            getMyOrganisation: 'OrganisationStore/GetMyOrganisation',
            setNotification: 'NotificationStore/SetNotification',
            setPatientAddress: 'AddressFormStore/SetAddress',
        }),

        async getPatientDetails() {
            try {
                this.isBusy = true
                let response = await axios.get(`${process.env.VUE_APP_AXIOS_PATIENT_BASE_URL}/api/v1/patients/${this.organisationId}/${this.$route.params.id}`)

                this.patient = response.data
                this.originalIdentifier = response.data.identifier

                if(response.data.dateOfBirth) {
                    let date = new Date(response.data.dateOfBirth)
                    this.dayOfBirthValue = date.getUTCDate();
                    this.monthOfBirthValue = date.getUTCMonth() + 1;
                    this.yearOfBirthValue = date.getUTCFullYear();
                }

                if (response.data.height) {
                    this.feet = this.convertCentimetresToFeet(response.data.height)
                }
                if (response.data.height) {
                    this.inches = this.convertCentimetresToInches(response.data.height)
                }
                if (response.data.weight) {
                    this.stones = this.convertKilosToStones(response.data.weight)
                }
                if (response.data.weight) {
                    this.pounds = this.convertKilosToPounds(response.data.weight)
                }

                if(response.data.primaryEthnicity != null) {
                    this.getPrimaryEthnicity()
                }

                if(response.data.secondaryEthnicity != null) {
                    this.getSecondaryEthnicity()
                }

                if(response.data.tertiaryEthnicity != null) {
                    this.getTertiaryEthnicity()
                }

                if (this.patient.address.countryCode === 'GB' || this.patient.address.countryCode === 'QA' || this.patient.address.countryCode === 'AE') {
                    if (!this.patient.address.provinceName) this.patient.address.provinceName = this.patient.address.district
                }
                if (this.patient.address.countryCode === 'US') {
                    if (!this.patient.address.provinceName) this.patient.address.provinceName = this.patient.address.state
                }
                this.setPatientAddress(this.patient.address)
            }
            catch(err) {
                this.setNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Sorry, the patient data could not be fetched'
                })
            }
            finally {
                this.isBusy = false
                this.loading = false
            }
        }, 

        async getPatientTests() {
            let searchModel = {
                organisationId: this.organisationId,
                patientId: this.$route.params.id,
                pageSize: 100,
                offset: 0
            }

            let response = await axios.post(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/search`, searchModel)

            this.patientTests = response.data.results.filter(t => {
                return t.isActive
            })
        },

        async getPatientNotes() {
            let response = await axios.get(`${process.env.VUE_APP_AXIOS_PATIENT_BASE_URL}/api/v1/patientnotes/${this.organisationId}/${this.$route.params.id}/100/0`)

            this.patientNotes = response.data.results
        },

        getPrimaryEthnicity() {
            const found = this.ethnicities.find(x => x.code == this.patient.primaryEthnicity)
            if(found) this.patient.primaryEthnicity = found
        },

        getSecondaryEthnicity() {
            const primary = this.ethnicities.find(x => x.code == this.patient.primaryEthnicity.code)
            if(!primary) return null

            const secondary = primary.secondaryEthnicityDefinitions.find(x => x.code == this.patient.secondaryEthnicity)
            if(secondary) this.patient.secondaryEthnicity = secondary
        },

        getTertiaryEthnicity() {
            const primary = this.ethnicities.find(x => x.code == this.patient.primaryEthnicity.code)
            if(!primary) return null

            const secondary = primary.secondaryEthnicityDefinitions.find(x => x.code == this.patient.secondaryEthnicity.code)
            if(!secondary) return null

            const tertiary = secondary.tertiaryEthnicityDefinitions.find(x => x.code == this.patient.tertiaryEthnicity)
            if(tertiary) this.patient.tertiaryEthnicity = tertiary
        },
        
        clearEthnicities(clearAll) {
            if(clearAll) {
                this.patient.secondaryEthnicity = null
                this.patient.tertiaryEthnicity = null
            } else {
                this.patient.tertiaryEthnicity = null
            }
        },

        async handlePatientIdentifierFocusOut() {
            if(!this.patient.identifier || !this.patient.identifier.match(this.patientIdentifierRegex)) return

            const identifier = normalisePatientIdentifier(this.patient.identifier)
            const isTaken = await this.isPatientIdentifierTaken(identifier)
            const outcome = identifierCheckOutcome(identifier, this.patient.identifier, isTaken)
            if(outcome === IDENTIFIER_CHECK_OUTCOME.RECHECK) return

            if(outcome === IDENTIFIER_CHECK_OUTCOME.DUPLICATE) {
                this.$set(this.errors, 'identifier', [this.duplicatePatientIdentifierMessage])
            } else if(this.errors.identifier && this.errors.identifier[0] === this.duplicatePatientIdentifierMessage) {
                this.$delete(this.errors, 'identifier')
            }
        },

        async isPatientIdentifierTaken(identifier) {
            if(!hasPatientIdentifierChanged(this.originalIdentifier, identifier)) return false

            this.isPatientIdentifierBusy = true
            try {
                const foundPatient = await this.checkPatientIdentifier(identifier)
                return isIdentifierTakenByAnotherPatient(foundPatient, this.$route.params.id)
            } finally {
                this.isPatientIdentifierBusy = false
            }
        },

        async updatePatient() {
            if(this.isCheckingIdentifier) return

            this.checkAddress()
            this.checkForm()

            if(!Object.keys(this.errors).length && !Object.keys(this.addressErrors).length) {
                const identifier = this.patient.identifier
                this.isCheckingIdentifier = true
                let isTaken
                try {
                    isTaken = await this.isPatientIdentifierTaken(identifier)
                } finally {
                    this.isCheckingIdentifier = false
                }
                const outcome = identifierCheckOutcome(identifier, this.patient.identifier, isTaken)
                if(outcome === IDENTIFIER_CHECK_OUTCOME.RECHECK) return this.updatePatient()
                if(outcome === IDENTIFIER_CHECK_OUTCOME.DUPLICATE) return this.$set(this.errors, 'identifier', [this.duplicatePatientIdentifierMessage])

                if(this.yearOfBirthValue != null && this.monthOfBirthValue != null && this.dayOfBirthValue != null) {
                    let date = new Date(Date.UTC(this.yearOfBirthValue, (this.monthOfBirthValue - 1), this.dayOfBirthValue, 0 ,0 ,0, 0))
                    this.patient.dateOfBirth = date.toUTCString()
                } else {
                    this.patient.dateOfBirth = null
                }
                
                this.$modal.show(ConfirmPatientDetailsModal, {patient: this.patient, stones: this.stones, pounds: this.pounds, feet: this.feet, inches: this.inches, updateMode: true})
            }
        },

        checkForm() {
            this.errors = {}

            if(this.patient.identifier != null) {
                this.patient.identifier = normalisePatientIdentifier(this.patient.identifier)
            }

            if(!this.patient.title && this.patient.title < 0) {
                this.errors.title = ['The title field is required']
            }

            if(!this.patient.firstName) {
                this.errors.firstName = ['The first name field is required']
            }

            if(!this.patient.surname) {
                this.errors.surname = ['The surname field is required']
            }

            if(this.organisationId != this.organisationIdException && !this.dayOfBirthValue) {
                this.errors.dayOfBirth = ['The day of birth field is required']
            }

            if(this.organisationId != this.organisationIdException && !this.monthOfBirthValue) {
                this.errors.monthOfBirth = ['The month of birth field is required']
            }

            if(this.organisationId != this.organisationIdException && !this.yearOfBirthValue) {
                this.errors.yearOfBirth = ['The year of birth field is required']
            }

            if(this.organisationId != this.organisationIdException && this.dayOfBirthValue && this.monthOfBirthValue && this.yearOfBirthValue) {
                let date = new Date(this.yearOfBirthValue, (this.monthOfBirthValue - 1), this.dayOfBirthValue, 0 ,0 ,0, 0)
                
                if(this.$moment(date).isSameOrAfter()) {
                    this.errors.dateOfBirth = ['This date cannot be in the future']
                }
            }

            if(!this.patient.identifier) {
                this.errors.identifier = [`${this.patientIdentifierErrorMessage}`]
            }

            if(this.patient.identifier != null && !this.patient.identifier.match(this.patientIdentifierRegex)) {
                this.errors.identifier = [`${this.patientIdentifierErrorMessage}`]
            }

            if(!this.patient.phone) {
                this.errors.phone = ['The phone number field is required']
            }

            if(this.patient.phone != null && !isValidPhoneNumber(this.patient.phone, this.patient.address.countryCode)) {
                this.errors.phone = [phoneNumberTooShortMessage(this.patient.address.countryCode)]
            }

            if(!this.patient.email) {
                this.errors.email = ['The email field is required']
            }

            if(this.organisationId != this.organisationIdException && (this.patient.height == null || this.patient.height < 50 || this.patient.height > 250)) {
                this.errors.height = ['The height field must be between 50 & 250cm']
            }

            if(this.organisationId != this.organisationIdException && (this.patient.weight == null || this.patient.weight < 31.75 || this.patient.weight > 254)) {
                this.errors.weight = ['The weight field must be between 31.75 & 254kg']
            }

            if(!this.patient.gender && this.patient.gender < 0) {
                this.errors.gender = ['The gender field is required']
            }

            if(this.patient.primaryEthnicity == null) {
                this.errors.primaryEthnicity = ['The primary ethnicity field is required']
            }

            if(this.patient.primaryEthnicity != null && this.patient.primaryEthnicity.secondaryEthnicityDefinitions && this.patient.primaryEthnicity.secondaryEthnicityDefinitions.length && this.patient.secondaryEthnicity == null) {
                this.errors.secondaryEthnicity = ['The secondary ethnicity field is required']
            }

            if(this.patient.secondaryEthnicity != null && this.patient.secondaryEthnicity.tertiaryEthnicityDefinitions && this.patient.secondaryEthnicity.tertiaryEthnicityDefinitions.length && this.patient.tertiaryEthnicity == null) {
                this.errors.tertiaryEthnicity = ['The tertiary ethnicity field is required']
            }
        }
    }
}
</script>